import Logo from "@assets/logos/logo.svg";
import PaySonicLogo from "@assets/logos/PaySonic.svg";
import PaysonicNoise from "@assets/noise/paysonic-noise.svg";
import SignUpNoise from "@assets/noise/tylt-noise.svg";
import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import {
  isPaysonicHost,
  isPrimeBrazilOnlyHost,
} from "src/config/deploymentConfig";
import { getApiErrorDisplay } from "@services/api/apiError";
import { authenticationApiService } from "../../../services/apis/Authentication";
import { sendOtpResponse } from "../../../services/models/Authentication/Authentication";

const MobileLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const isPrimeBrazilOnly = isPrimeBrazilOnlyHost();
  const isPaysonic = isPaysonicHost();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      continueButtonRef.current?.click();
    }
  };

  const checkValidEmail = () => {
    if (email.length > 0) {
      const emailRegex = new RegExp(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      );
      if (!emailRegex.test(email)) {
        return false;
      } else {
        return true;
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setIsButtonLoading(true);
      const response: sendOtpResponse =
        await authenticationApiService.sendOtp(email);
      const csrfToken = response.data.csrfToken;
      secureLocalStorage.setItem("loginEmail", email);
      secureLocalStorage.setItem("loginCsrfToken", csrfToken);
      navigate("/verifyOtp", { state: { csrfToken, email } });
    } catch (error) {
      setError(getApiErrorDisplay(error));
    } finally {
      setIsButtonLoading(false);
    }
  };

  const checkLogin = () => {
    const jwt = secureLocalStorage.getItem("jwt");
    if (jwt) {
      navigate("/");
    }
  };

  useEffect(() => {
    checkLogin();
  }, []);

  return (
    <div className="flex flex-col bg-[#ffffff] min-h-screen">
      {/* Blue Carousel Banner — Top */}
      <div
        className="w-full bg-text-navbar-active py-10 px-6"
        style={{
          backgroundImage: `url(${isPaysonic ? PaysonicNoise : SignUpNoise})`,
        }}
      >
        <div className="flex flex-col items-center text-[#ffffff]">
          <div className="font-bold text-[22px] leading-[34px] text-center">
            <p>
              {isPrimeBrazilOnly
                ? "ADMIN PANEL"
                : isPaysonic
                  ? "PAYSONIC ADMIN PANEL"
                  : "TYLT ADMIN PANEL"}
            </p>
            <p>
              MANAGE OPERATIONS <br /> WITH EASE.
            </p>
          </div>
          <div className="text-center mt-3 px-4">
            <p className="font-medium text-[13px] leading-[20px]">
              Manage your operations efficiently. Monitor transactions, oversee
              users, and configure platform settings with comprehensive admin
              tools.
            </p>
          </div>
        </div>
      </div>

      {/* Form Section — Below */}
      <div className="flex flex-col justify-start items-center px-4 pt-8 flex-1">
        <div className="flex flex-row mb-[24px] items-center justify-center">
          <img
            src={isPaysonic ? PaySonicLogo : Logo}
            alt="logo"
            className="h-24 w-24"
          />
        </div>
        <div className="p-[16px] w-full max-w-[400px] rounded-[12px]">
          <div className="text-text-base text-[14px] font-semibold mb-4 items-center flex justify-center">
            Enter into your admin account!
          </div>
          <div className="flex flex-col gap-[16px]">
            <GlobalTextInputField
              placeholder="Email id"
              fontSize="14px"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(e.target.value)
              }
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="my-4">
            <CustomButton
              ref={continueButtonRef}
              text="CONTINUE"
              onClick={handleSubmit}
              isActive={checkValidEmail() ? true : false}
              isButtonLoading={isButtonLoading}
            />
          </div>
          {error && (
            <div className="flex flex-row items-center">
              <span className="material-symbols-outlined mr-1 text-[20px] text-text-status-red">
                warning
              </span>
              <p className="text-text-status-red text-[12px] font-medium text-center">
                {error}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileLogin;
